import { useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_DOCUMENTS_POLICY } from '../../../gql/queries/policiesApiQueries'
import { CREATE_UPDATE_ATTACHMENTS_TAG } from '../../../gql/mutations/attachmentsTagMutations'
import { IResAllTagsDocuments } from '../../../types/policiesApi'
import { parseId } from '../../../helpers'
import { useIntl } from 'react-intl'
import { BACKEND_ERROR, RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

interface ITagNode {
  id: string
  tagName: string
}

interface IAttachmentsTagCreateUpdateResponse {
  attachmentsTagCreateUpdateMutation: {
    attachmentsTag?: { id: string; tagName: string }
    errors?: Array<{ field: string; messages: string[] }>
  }
}

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  // Se llama al crear un tag nuevo (con el id de la lista refrescada),
  // para auto-seleccionarlo en el formulario que abrió este modal
  onTagCreated?: (tag: ITagNode) => void
}

export const ModalManageTags: React.FC<IModalProps> = ({ isOpen, onClose, onTagCreated }) => {
  const intl = useIntl()
  const [newTagName, setNewTagName] = useState('')
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  const { data, loading, refetch } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  })

  const tags = data?.allAttachmentsTags.edges.map(({ node }) => node) || []

  const [saveTag, { loading: saving }] = useMutation<IAttachmentsTagCreateUpdateResponse>(
    CREATE_UPDATE_ATTACHMENTS_TAG
  )

  const handleCreate = async () => {
    const name = newTagName.trim()
    if (!name) return
    if (tags.some((tag) => tag.tagName.toLowerCase() === name.toLowerCase())) {
      toast.warning(intl.formatMessage(R_.warnTagExists))
      return
    }
    try {
      const result = await saveTag({ variables: { input: { tagName: name } } })
      const payload = result.data?.attachmentsTagCreateUpdateMutation
      if (payload?.errors?.length) {
        toast.error(
          intl.formatMessage(BACKEND_ERROR, {
            message:
              payload.errors[0].messages?.[0] || intl.formatMessage(R_.errCouldNotCreateTag),
          })
        )
        return
      }
      toast.success(intl.formatMessage(R_.toastTagCreated))
      setNewTagName('')
      // Refresca el cache global: los selects de filtros y modales se actualizan solos
      const refetched = await refetch()
      if (onTagCreated) {
        const created = refetched.data?.allAttachmentsTags.edges.find(
          ({ node }) => node.tagName === name
        )?.node
        if (created) onTagCreated(created)
      }
    } catch (err) {
      console.error('Error creating tag:', err)
      toast.error(intl.formatMessage(R_.errCreateTag))
    }
  }

  const handleRename = async (tag: ITagNode) => {
    const name = editingName.trim()
    if (!name || name === tag.tagName) {
      setEditingTagId(null)
      return
    }
    try {
      const result = await saveTag({
        variables: { input: { id: parseId(tag.id), tagName: name } },
      })
      const payload = result.data?.attachmentsTagCreateUpdateMutation
      if (payload?.errors?.length) {
        toast.error(
          intl.formatMessage(BACKEND_ERROR, {
            message:
              payload.errors[0].messages?.[0] || intl.formatMessage(R_.errCouldNotRenameTag),
          })
        )
        return
      }
      toast.success(intl.formatMessage(R_.toastTagRenamed))
      setEditingTagId(null)
      refetch()
    } catch (err) {
      console.error('Error renaming tag:', err)
      toast.error(intl.formatMessage(R_.errRenameTag))
    }
  }

  const handleClose = () => {
    setNewTagName('')
    setEditingTagId(null)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-tags" /> {intl.formatMessage(R_.manageTags)}
          {(loading || saving) && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Crear tag nuevo */}
        <div className="d-flex gap-2 mb-4">
          <Form.Control
            placeholder={intl.formatMessage(R_.newTagPlaceholder)}
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleCreate()
              }
            }}
            autoFocus
          />
          <Button variant="primary" onClick={handleCreate} disabled={saving || !newTagName.trim()}>
            <i className="bi bi-plus-lg me-1" /> {intl.formatMessage(R_.addTag)}
          </Button>
        </div>

        {/* Lista de tags existentes */}
        <h6 className="text-muted mb-2">
          {intl.formatMessage(R_.existingTags, {count: tags.length})}
        </h6>
        <div className="list-group" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {tags.length === 0 && !loading ? (
            <div className="list-group-item text-center text-muted small py-3">
              {intl.formatMessage(R_.noTagsYet)}
            </div>
          ) : (
            tags.map((tag) => (
              <div key={tag.id} className="list-group-item d-flex align-items-center gap-2 py-2">
                {editingTagId === tag.id ? (
                  <>
                    <Form.Control
                      size="sm"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleRename(tag)
                        }
                        if (e.key === 'Escape') setEditingTagId(null)
                      }}
                      autoFocus
                    />
                    <Button size="sm" variant="outline-primary" onClick={() => handleRename(tag)}>
                      <i className="bi bi-check-lg" />
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEditingTagId(null)}>
                      <i className="bi bi-x-lg" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="badge badge-light-primary border border-primary">
                      <i className="bi bi-tag me-1" />
                      {tag.tagName}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm p-0 border-0 text-muted ms-auto"
                      title={intl.formatMessage(R_.renameTag)}
                      onClick={() => {
                        setEditingTagId(tag.id)
                        setEditingName(tag.tagName)
                      }}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
        {intl.formatMessage(R_.done)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
